import { ColumnDef } from "@tanstack/react-table";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useGetPatientBookings } from "../../../../../api/usePatientBookingsQuery";
import { SequentialUnlockItem } from "api/types";
import {
  useGetAllIcbtCommunication,
  useGetAllProgress,
  useGetIcbtRequests,
} from "../../../../../api/useIcbtQueries";
import { useQueryErrorLogger } from "../../../../../hooks";
import { useGetAllProgramsAndTreatmentsQuery } from "../../../../../hooks/icbt/useGetAllProgramsAndTreatmentsQuery";
import { useGetQuestionnaireCountsPerProgramQuery } from "hooks/icbt/useGetQuestionnaireCountsPerProgramQuery";
import { useGetUserAnswersQuery } from "api/useQuestionnaireQueries";
import { Handle } from "hooks/useDnD/Handle";
import { useFlag } from "@mindlercare/unleash-client";
import {
  getDraggableRowIds,
  parseIcbtData,
  getIsSequentiallyLocked,
  getIsFeedbackLocked,
  getFeedbackLockReason,
  getIsRequestOwner,
  getRequestingPsychologistId,
} from "./utils";
import {
  programTitleCell,
  feedbackDateCell,
  patientNotesCell,
  icbtFeedbackCell,
  assignedCell,
} from "./components";

type UseIcbtTableDataProps = {
  patientId?: number;
  userId?: number;
  openPreviewModal: ({ programId }: { programId: string }) => void;
  icbtSequentialUnlock?: SequentialUnlockItem[];
};

export const useIcbtTableData = ({
  patientId,
  userId,
  openPreviewModal,
  icbtSequentialUnlock,
}: UseIcbtTableDataProps) => {
  const { t } = useTranslation();
  const {
    data: bookings,
    isLoading: bookingsLoading,
    error: bookingsError,
  } = useGetPatientBookings(
    patientId
      ? { patientId: patientId.toString(), psychologistId: "-1" }
      : undefined,
  );

  const sortableIcbtsEnabled = useFlag("PSYCHOLOGIST_SORTABLE_ICBTS");
  const feedbackLockEnabled = useFlag(
    "ICBT_LOCK_FEEDBACK_UNTIL_PATIENT_ACTIVE",
  );

  const {
    data: icbtRequests,
    isLoading: icbtRequestsIsLoading,
    error: icbtRequestsError,
  } = useGetIcbtRequests(userId);

  const {
    data: allProgress,
    isLoading: allProgressIsLoading,
    error: allProgressError,
  } = useGetAllProgress(userId?.toString());

  const {
    data: programsAndTreatments,
    isLoading: programsAndTreatmentsIsLoading,
    error: programsAndTreatmentsError,
  } = useGetAllProgramsAndTreatmentsQuery(undefined);

  const { data: allCommunication, isLoading: allCommunicationIsLoading } =
    useGetAllIcbtCommunication(userId);

  const { data: questionnaireCounts, isLoading: questionnaireCountsIsLoading } =
    useGetQuestionnaireCountsPerProgramQuery();

  const { data: userAnswers, isLoading: userAnswersIsLoading } =
    useGetUserAnswersQuery({
      userId: userId?.toString(),
      answerRequestType: "icbt",
    });

  const data = useMemo(() => {
    return parseIcbtData({
      allProgress,
      icbtRequests,
      programsAndTreatments,
      bookings,
      userAnswers,
      userId,
      allCommunication,
      t,
      questionnaireCounts,
      sortableIcbtsEnabled,
      feedbackLockEnabled,
      icbtSequentialUnlock,
    });
  }, [
    allProgress,
    icbtRequests,
    programsAndTreatments,
    bookings,
    userAnswers,
    userId,
    allCommunication,
    t,
    questionnaireCounts,
    sortableIcbtsEnabled,
    feedbackLockEnabled,
    icbtSequentialUnlock,
  ]);

  const draggableRowIds = useMemo(
    () => getDraggableRowIds(data.assignedTableRows),
    [data.assignedTableRows],
  );

  const assignedTableColumns = useMemo<
    ColumnDef<NonNullable<typeof data>["assignedTableRows"][number]>[]
  >(() => {
    return [
      {
        accessorKey: "dragHandle",
        header: "",
        cell: ({ row }) => {
          const isDraggable = draggableRowIds.has(row.original.id);
          const hasMultipleDraggableRows = draggableRowIds.size > 1;

          if (
            !isDraggable ||
            !hasMultipleDraggableRows ||
            !sortableIcbtsEnabled
          ) {
            return null;
          }

          return <Handle rowId={row.original.id} />;
        },
      },
      {
        accessorKey: "title",
        header: "PROGRAM_TITLE",
        cell: ({ getValue, row }) =>
          programTitleCell({
            t,
            getValue,
            openPreviewModal,
            status: row.original.status,
            isLocked: row.original.isLocked,
            nextScheduledExecution: row.original.nextScheduledExecution,
          }),
      },
      {
        accessorKey: "feedbackDate",
        header: "FEEDBACK_DATE",
        cell: ({ getValue }) => feedbackDateCell(getValue),
      },
      {
        accessorKey: "patientNotes",
        header: "PATIENT_NOTES",
        cell: ({ getValue }) => patientNotesCell(getValue),
      },
      {
        accessorKey: "icbtFeedbackState",
        header: "ICBT_FEEDBACK",
        cell: ({ getValue, row }) =>
          icbtFeedbackCell({
            getValue,
            isFeedbackLocked: row.original.isFeedbackLocked,
            feedbackLockReason: row.original.feedbackLockReason,
            t,
          }),
      },
      {
        accessorKey: "assigned",
        header: "ASSIGNED",
        cell: ({ getValue }) => assignedCell(getValue),
      },
    ];
  }, [draggableRowIds, sortableIcbtsEnabled, t, openPreviewModal]);

  const selfExploredTableColumns = useMemo<
    ColumnDef<NonNullable<typeof data>["selfExploredTableRows"][number]>[]
  >(() => {
    return [
      {
        accessorKey: "title",
        header: "PROGRAM_TITLE",
        cell: ({ getValue, row }) => {
          return programTitleCell({
            t,
            getValue,
            openPreviewModal,
            status: row.original.status,
            isLocked: row.original.isLocked,
            nextScheduledExecution: row.original.nextScheduledExecution,
          });
        },
      },
      {
        accessorKey: "startedAt",
        header: "STARTED_AT",
        cell: (info) => info.getValue(),
      },
    ];
  }, [openPreviewModal, t]);

  // Derived data fetching states
  const isLoading =
    bookingsLoading ||
    icbtRequestsIsLoading ||
    allProgressIsLoading ||
    programsAndTreatmentsIsLoading ||
    allCommunicationIsLoading ||
    questionnaireCountsIsLoading ||
    userAnswersIsLoading;

  const possiblyErrors = {
    bookingsError,
    icbtRequestsError,
    allProgressError,
    programsAndTreatmentsError,
  };

  // Not sure it is correct usage, but lets try...
  const isError = useQueryErrorLogger(
    possiblyErrors,
    "Failed to load data in patient cards icbt page",
    {
      team: "treatment",
      tag: "assignIcbt",
      patientUserId: userId,
    },
  );

  return {
    data,
    assignedTableColumns,
    selfExploredTableColumns,
    isLoading,
    possiblyErrors,
    isError,
  };
};

export * from "./types";
export {
  getIsSequentiallyLocked,
  getIsFeedbackLocked,
  getFeedbackLockReason,
  getIsRequestOwner,
  getRequestingPsychologistId,
};
